/**
 * @fileoverview Prompt template for chapter-splitter-v2.
 */

export const prompt = `
You are a master fiction editor with an expert sense of narrative structure and pacing.

## TASK INSTRUCTIONS:
Your task is to intelligently split the provided chapter into two new, coherent chapters.

{{#if chapterContent}}
### MODE: CONTENT SPLIT
The chapter has existing prose content. You must analyze and split this content.

1.  **Analyze the Content:** Read the provided chapter and identify a natural breaking point. This could be a scene change, a shift in perspective, a significant plot development, or a moment of high tension suitable for a cliffhanger.
2.  **Determine the Split:** Based on your analysis, the story context, and any user instructions, decide where to divide the chapter.
3.  **Outline Two New Chapters:** For each of the two new chapters, you must generate a complete chapter outline. This includes:
    *   A new, compelling title.
    *   A summary of the key events for that new chapter.
    *   An assessment of the chapter's goals (how it advances the plot, develops characters, and raises stakes).
    *   A recommendation for pacing.
    *   A suggested POV character if applicable.
4.  **Rewrite for Cohesion:** It is crucial that you also divide the original chapter's prose content. The first new chapter should have a revised ending that provides a satisfying conclusion for that segment. The second new chapter must have a revised opening that re-establishes the context and smoothly continues the narrative.
{{else}}
### MODE: OUTLINE SPLIT
The chapter has no written content yet—only metadata and context. You must create two chapter outlines based on the chapter's planned events, story context, and any user guidance.

1.  **Analyze the Chapter Plan:** Review the chapter's key events, goals, and position in the story to understand what this chapter is supposed to accomplish.
2.  **Determine the Split:** Based on the planned events and story context, identify a logical way to divide this chapter's scope into two separate chapters. Consider natural narrative breaks, pacing, and tension points.
3.  **Outline Two New Chapters:** For each of the two new chapters, generate a complete chapter outline. This includes:
    *   A new, compelling title that reflects its content.
    *   A summary of the key events for that new chapter (distributed from the original chapter's planned events).
    *   An assessment of the chapter's goals (how it advances the plot, develops characters, and raises stakes).
    *   A recommendation for pacing.
    *   A suggested POV character if applicable.
4.  **Content Output:** Since there is no existing prose to split, return empty strings for the contents array. The output schema still requires two content strings—provide two empty strings: ["", ""].
{{/if}}

<<<userMessage>>>

## PRIMARY TASK CONTEXT

{{#if chapterContent}}
**CHAPTER CONTENT**
{{{chapterContent}}}
{{else}}
**NOTE:** This chapter has no written content. Use the chapter context and story context below to plan the split.
{{/if}}

<<<chapterContext>>>

## SECONDARY TASK CONTEXT

<<<storyContext>>>

<<<charactersCurrent>>>

## OUTPUT INSTRUCTIONS:

**Key Events (Seed)**: Write 1-2 sentences per chapter that summarise the core events. Use neutral, objective style — no dialogue, no sensory details. This seed will be expanded into detailed key events in a follow-up step, so focus on WHAT happens, not the detail of HOW.

**Chapter Goals**: For each goal field (plotDevelopment, charDevelopment, stakesAndTension), write 1-2 sentences that reference specific events from the chapter.

Provide your output in the required JSON format.
`;
